import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { organizationApi, Organization, EffectiveSetting, SettingValue } from '../../services/api';
import { CodeDisplayModal } from '../../components/CodeDisplayModal';

type SettingType = 'boolean' | 'number' | 'string' | 'option';

interface EditingSetting {
  key: string;
  value: boolean | number | string;
  type: SettingType;
  options?: string[]; // Available options for 'option' type
}

export function OrganizationDetails(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [organization, setOrganization] = useState<Organization | null>(null);
  const [effectiveSettings, setEffectiveSettings] = useState<Record<string, EffectiveSetting>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Code regeneration
  const [generatedCode, setGeneratedCode] = useState<string | null>(null);

  // Editing state
  const [editingSetting, setEditingSetting] = useState<EditingSetting | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const loadOrganization = async () => {
    if (!id) return;

    try {
      setIsLoading(true);
      const response = await organizationApi.getById(parseInt(id, 10));
      setOrganization(response.organization);
      setEffectiveSettings(response.organization.effectiveSettings || {});
    } catch (err: any) {
      setError(err.message || 'Fehler beim Laden');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadOrganization();
  }, [id]);

  const handleRegenerateCode = async () => {
    if (!organization) return;
    if (!window.confirm(`Zugangscode für "${organization.name}" wirklich erneuern?\n\nDer alte Code wird sofort ungültig!`)) return;

    try {
      const response = await organizationApi.regenerateCode(organization.id);
      if (response.accessCode) {
        setGeneratedCode(response.accessCode);
      }
    } catch (err: any) {
      setError(err.message || 'Fehler beim Erneuern des Codes');
    }
  };

  const startEditSetting = (key: string, setting: EffectiveSetting) => {
    setEditingSetting({
      key,
      value: setting.value,
      type: setting.type,
      options: setting.options,
    });
    setSuccessMessage('');
  };

  const cancelEdit = () => {
    setEditingSetting(null);
  };

  const saveSetting = async () => {
    if (!editingSetting || !organization) return;

    setIsSaving(true);
    try {
      const settingValue: SettingValue = {
        type: editingSetting.type,
        value: editingSetting.value,
      };

      const response = await organizationApi.updateSettings(organization.id, {
        [editingSetting.key]: settingValue,
      });

      setEffectiveSettings(response.effectiveSettings);
      setEditingSetting(null);
      setSuccessMessage(`Einstellung "${editingSetting.key}" gespeichert`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Fehler beim Speichern');
    } finally {
      setIsSaving(false);
    }
  };

  const resetSetting = async (key: string) => {
    if (!organization) return;
    if (!window.confirm(`Einstellung "${key}" auf den globalen Standardwert zurücksetzen?`)) return;

    try {
      const response = await organizationApi.resetSetting(organization.id, key);
      setEffectiveSettings(response.effectiveSettings);
      setSuccessMessage(`Einstellung "${key}" zurückgesetzt`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Fehler beim Zurücksetzen');
    }
  };

  const renderSettingInput = () => {
    if (!editingSetting) return null;

    switch (editingSetting.type) {
      case 'boolean':
        return (
          <select
            className="setting-input"
            value={editingSetting.value ? 'true' : 'false'}
            onChange={(e) => setEditingSetting({
              ...editingSetting,
              value: e.target.value === 'true',
            })}
          >
            <option value="true">Ja / Aktiviert</option>
            <option value="false">Nein / Deaktiviert</option>
          </select>
        );
      case 'number':
        return (
          <input
            type="number"
            className="setting-input"
            value={editingSetting.value as number}
            onChange={(e) => setEditingSetting({
              ...editingSetting,
              value: parseFloat(e.target.value) || 0,
            })}
          />
        );
      case 'option':
        return (
          <select
            className="setting-input"
            value={String(editingSetting.value)}
            onChange={(e) => setEditingSetting({
              ...editingSetting,
              value: e.target.value,
            })}
          >
            {editingSetting.options?.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        );
      case 'string':
      default:
        return (
          <input
            type="text"
            className="setting-input"
            value={editingSetting.value as string}
            onChange={(e) => setEditingSetting({
              ...editingSetting,
              value: e.target.value,
            })}
          />
        );
    }
  };

  const formatValue = (setting: EffectiveSetting): string => {
    if (setting.type === 'boolean') {
      return setting.value ? 'Ja' : 'Nein';
    }
    return String(setting.value);
  };

  if (isLoading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Laden...</p>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="admin-page">
        <div className="error-message">Markt nicht gefunden</div>
        <button className="btn btn-secondary" onClick={() => navigate('/admin/organizations')}>
          Zurück zur Liste
        </button>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="page-header">
        <div>
          <button className="btn btn-secondary btn-small" onClick={() => navigate('/admin/organizations')}>
            ← Zurück
          </button>
          <h1 style={{ marginTop: '1rem' }}>{organization.name}</h1>
          <p className="org-subtitle">Store-ID: <code>{organization.storeId}</code></p>
        </div>
        <div className="header-actions">
          <button className="btn btn-warning" onClick={handleRegenerateCode}>
            Zugangscode erneuern
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      <div className="details-section">
        <h2>Markt-Informationen</h2>
        <div className="info-grid">
          <div className="info-item">
            <label>Name</label>
            <span>{organization.name}</span>
          </div>
          <div className="info-item">
            <label>Store-ID</label>
            <span><code>{organization.storeId}</code></span>
          </div>
          <div className="info-item">
            <label>Status</label>
            <span className={`status-badge ${organization.isActive ? 'active' : 'inactive'}`}>
              {organization.isActive ? 'Aktiv' : 'Inaktiv'}
            </span>
          </div>
          <div className="info-item">
            <label>Erstellt am</label>
            <span>{new Date(organization.createdAt).toLocaleDateString('de-DE')}</span>
          </div>
        </div>
      </div>

      <div className="details-section">
        <h2>Einstellungen</h2>
        <p className="section-description">
          Diese Einstellungen können individuell für diesen Markt überschrieben werden.
          Nicht überschriebene Einstellungen verwenden den globalen Standardwert.
        </p>

        <div className="settings-list">
          {Object.entries(effectiveSettings).map(([key, setting]) => (
            <div key={key} className={`setting-item ${setting.isOverridden ? 'overridden' : ''}`}>
              <div className="setting-header">
                <code className="setting-key">{key}</code>
                <span className={`type-badge ${setting.type}`}>{setting.type}</span>
                {setting.isOverridden && (
                  <span className="override-badge">Überschrieben</span>
                )}
                {!setting.isOverridden && (
                  <span className="global-badge">Global</span>
                )}
              </div>

              <p className="setting-description">{setting.description}</p>

              {setting.type === 'option' && setting.options && setting.options.length > 0 && (
                <div className="setting-options">
                  <span className="options-label">Verfügbare Optionen:</span>
                  {setting.options.map(opt => (
                    <span
                      key={opt}
                      className={`option-badge ${String(setting.value) === opt ? 'selected' : ''}`}
                    >
                      {opt}
                    </span>
                  ))}
                </div>
              )}

              {editingSetting?.key === key ? (
                <div className="setting-edit-row">
                  {renderSettingInput()}
                  <div className="setting-edit-actions">
                    <button
                      className="btn btn-primary btn-small"
                      onClick={saveSetting}
                      disabled={isSaving}
                    >
                      {isSaving ? 'Speichern...' : 'Speichern'}
                    </button>
                    <button
                      className="btn btn-secondary btn-small"
                      onClick={cancelEdit}
                    >
                      Abbrechen
                    </button>
                  </div>
                </div>
              ) : (
                <div className="setting-value-row">
                  <span className="setting-value">{formatValue(setting)}</span>
                  <div className="setting-actions">
                    <button
                      className="btn btn-small"
                      onClick={() => startEditSetting(key, setting)}
                    >
                      Bearbeiten
                    </button>
                    {setting.isOverridden && (
                      <button
                        className="btn btn-small btn-secondary"
                        onClick={() => resetSetting(key)}
                        title="Auf globalen Wert zurücksetzen"
                      >
                        Zurücksetzen
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {generatedCode && (
        <CodeDisplayModal
          code={generatedCode}
          title={`Neuer Zugangscode für "${organization.name}"`}
          onClose={() => setGeneratedCode(null)}
        />
      )}
    </div>
  );
}

export default OrganizationDetails;
